/**
 * Owner Letter DOCX Generation Utility
 * 
 * Generates DOCX letters for contacting copropriétaires with proper content
 * using the 4 unified templates (same for all owner types).
 * This allows end users to easily edit the document.
 */

import {
  AlignmentType,
  BorderStyle,
  Document,
  ExternalHyperlink,
  ImageRun,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';
import { saveAs } from 'file-saver';

import type { Company } from '../@types/Company';
import type { Owner } from '../@types/Owner';
import type { FirstContactTemplateType } from '../components/Prospecting/OwnerCommunication/utils/emailContentGenerator';
import {
  FIRST_CONTACT_TEMPLATE,
  isTransactionWithoutDpeTemplate,
  resolveReferentName,
} from '../components/Prospecting/OwnerCommunication/utils/emailContentGenerator';
import { buildShortConsentUrl } from './shortUrl';

const CIVILITE: Record<number, string> = {
  1: 'M.',
  2: 'Mme',
  3: 'Société',
};

const getOwnerFullName = (owner: Owner): string => {
  const civility = owner.civility ? CIVILITE[owner.civility] : '';
  return `${civility} ${owner.firstname || ''} ${owner.lastname || ''}`.trim();
};

const getPropertyAddress = (owner: Owner): string => {
  if (owner.dpe?.copro?.addresses && owner.dpe.copro.addresses.length > 0) {
    const a = owner.dpe.copro.addresses[0];
    return `${a.address || ''}, ${a.postalCode || ''} ${a.city || ''}`.trim();
  }
  if (owner.incomingCallCopro?.addresses && owner.incomingCallCopro.addresses.length > 0) {
    const a = owner.incomingCallCopro.addresses[0];
    return `${a.address || ''}, ${a.postalCode || ''} ${a.city || ''}`.trim();
  }
  return `${owner.address || ''}, ${owner.postalCode || ''} ${owner.city || ''}`.trim();
};

const getMailingAddress = (owner: Owner): { street: string; postalCode: string; city: string } => {
  const isResident = owner.livingIn === true;
  
  if (isResident) {
    if (owner.dpe?.copro?.addresses && owner.dpe.copro.addresses.length > 0) {
      const a = owner.dpe.copro.addresses[0];
      return { street: a.address || '', postalCode: a.postalCode || '', city: a.city || '' };
    }
    if (owner.incomingCallCopro?.addresses && owner.incomingCallCopro.addresses.length > 0) {
      const a = owner.incomingCallCopro.addresses[0];
      return { street: a.address || '', postalCode: a.postalCode || '', city: a.city || '' };
    }
  }
  
  return { street: owner.address || '', postalCode: owner.postalCode || '', city: owner.city || '' };
};

const fetchImageAsBase64 = async(imageUrl: string): Promise<string | null> => {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) {return null;}
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        resolve(base64.split(',')[1]);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const fetchQRCodeAsBase64 = async(url: string, size: number = 150): Promise<string | null> => {
  const encodedUrl = encodeURIComponent(url);
  const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodedUrl}`;
  return fetchImageAsBase64(qrApiUrl);
};

const mmToTwips = (mm: number) => Math.round(mm * 56.7);

/**
 * Create the recipient address block positioned for DL windowed envelope.
 * (La Poste / NF Z 10-011)
 */
const createAddressBlock = (
  owner: Owner,
  mailingAddress: { street: string; postalCode: string; city: string },
): Table => {
  const spacerWidth = mmToTwips(90);
  const addressWidth = mmToTwips(85);
  
  const formatPostalLine = (postalCode: string, city: string): string => {
    const removeAccents = (str: string): string =>
      str.normalize('NFD').replaceAll(/[\u0300-\u036f]/g, '');
    return removeAccents(`${postalCode} ${city}`).toUpperCase();
  };

  const noBorder = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
  const noBorders = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };
  
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: { ...noBorders, insideHorizontal: noBorder, insideVertical: noBorder },
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: spacerWidth, type: WidthType.DXA },
            borders: noBorders,
            children: [new Paragraph({ children: [] })],
          }),
          new TableCell({
            width: { size: addressWidth, type: WidthType.DXA },
            borders: noBorders,
            children: [
              new Paragraph({
                children: [new TextRun({ text: getOwnerFullName(owner), bold: true, size: 22, font: 'Arial' })],
                spacing: { after: 60 },
              }),
              new Paragraph({
                children: [new TextRun({ text: mailingAddress.street.toUpperCase(), size: 22, font: 'Arial' })],
                spacing: { after: 60 },
              }),
              new Paragraph({
                children: [new TextRun({ text: formatPostalLine(mailingAddress.postalCode, mailingAddress.city), size: 22, font: 'Arial' })],
              }),
            ],
          }),
        ],
      }),
    ],
  });
};

/**
 * Build DOCX paragraphs for the template-specific body section.
 * Mirrors the 4 options from the docx spec exactly.
 */
const buildTemplateBodyParagraphs = (
  templateType: FirstContactTemplateType,
  consentUrl: string,
): Paragraph[] => {
  const justified = AlignmentType.JUSTIFIED;
  const sp = { after: 240 };

  const consentParagraph = (prefix: string) => new Paragraph({
    alignment: justified,
    children: [
      new TextRun({ text: `${prefix} ` }),
      new ExternalHyperlink({
        link: consentUrl,
        children: [new TextRun({ text: consentUrl, color: '0066CC', underline: {} })],
      }),
    ],
    spacing: sp,
  });

  switch (templateType) {
    case FIRST_CONTACT_TEMPLATE.REFERENT_ONLY:
      return [
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'En tant que référent mutation location, je peux vous aider à obtenir si vous avez un projet immobilier :' })],
          spacing: sp,
        }),
        new Paragraph({
          children: [new TextRun({ text: '- L\'état daté, s\'agissant d\'une vente' })],
          spacing: { after: 60 },
        }),
        new Paragraph({
          children: [new TextRun({ text: '- L\'extrait pertinent du règlement de copropriété s\'agissant d\'une location.' })],
          spacing: sp,
        }),
        consentParagraph('Je me tiens à votre disposition pour vous fournir toute précision quant à ces documents, selon que vous projetez de vendre ou de louer votre appartement :'),
      ];

    case FIRST_CONTACT_TEMPLATE.DPE_REPORT:
      return [
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Si vous souhaitez obtenir de plus amples informations sur le dispositif de DPE collectif institué par le Code de la construction et de l\'habitation, ainsi que sur les différents DPE des appartements de votre immeuble et leur impact potentiel sur la valeur de votre bien, je me tiens à votre disposition.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Je peux notamment vous transmettre le rapport des DPE réalisés dans votre immeuble depuis 2021. Ce document vous permettra de situer la performance énergétique de votre appartement par rapport aux autres lots de la copropriété et d\'anticiper les évolutions réglementaires à venir.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Vous pourrez constater que les étiquettes énergétiques peuvent varier d\'un appartement à l\'autre. Ces différences s\'expliquent notamment par l\'étage, l\'exposition, l\'isolation ou encore la configuration du logement. Nous pourrons bien entendu revenir ensemble sur ces éléments si vous le souhaitez.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Par ailleurs, je me permets d\'attirer votre attention sur le fait que tout copropriétaire vendeur ou bailleur doit obligatoirement fournir des documents que le syndic est en mesure de vous préparer : un état daté dans le cadre d\'une vente, un extrait pertinent du règlement de copropriété dans le cadre d\'une location.' })],
          spacing: sp,
        }),
        consentParagraph('Je reste à votre disposition pour échanger et vous apporter toute précision sur ces obligations si vous avez un projet immobilier :'),
      ];

    case FIRST_CONTACT_TEMPLATE.COPRO_REPORT:
      return [
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Si vous souhaitez obtenir de plus amples informations, je peux également vous transmettre un rapport détaillé sur votre copropriété, intégrant les informations relatives aux derniers DPE réalisés dans la copropriété ainsi que celles relatives aux ventes intervenues au cours des quatre dernières années, tant au sein de l\'immeuble que dans un rayon d\'environ 150 mètres.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Ce document ne constitue pas un avis de valeur de votre appartement, dans la mesure où de nombreux critères spécifiques doivent être pris en compte pour établir une estimation précise. Il vous permettra néanmoins de mieux appréhender les tendances du marché local et le positionnement de votre bien.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Par ailleurs, je me permets d\'attirer votre attention sur le fait que tout copropriétaire vendeur ou bailleur doit obligatoirement fournir des documents que le syndic est en mesure de vous préparer : un état daté dans le cadre d\'une vente, un extrait pertinent du règlement de copropriété dans le cadre d\'une location.' })],
          spacing: sp,
        }),
        consentParagraph('Je reste à votre disposition pour échanger et vous apporter toute précision sur ces obligations si vous avez un projet immobilier :'),
      ];

    case FIRST_CONTACT_TEMPLATE.BOTH_REPORTS:
      return [
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Si vous souhaitez obtenir de plus amples informations et avoir une vision complète, je peux vous transmettre conjointement :' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: '1 - le rapport des DPE réalisés dans votre immeuble depuis 2021 : Vous pourrez constater que les étiquettes énergétiques (lettres des DPE) peuvent varier d\'un appartement à l\'autre au sein d\'un même immeuble.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: '2 - le rapport détaillé de présentation de la copropriété : Ce second document intègre les ventes intervenues au cours des quatre dernières années, tant dans l\'immeuble que dans un rayon d\'environ 150 mètres. Il ne constitue pas un avis de valeur, en revanche, il vous apportera une lecture claire des dynamiques du marché local et du positionnement de votre bien.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Ces rapports vous permettront notamment :\n• de situer votre bien par rapport aux autres appartements\n• d\'identifier d\'éventuels écarts de performance énergétique\n• et de mieux comprendre son positionnement sur le marché local' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Par ailleurs, tout copropriétaire doit fournir un document que le syndic est seul en mesure de préparer dans le cadre d\'une vente : l\'état daté.' })],
          spacing: sp,
        }),
        consentParagraph('Je reste à votre disposition pour échanger sur ces documents et vous apporter toute précision sur ces obligations si vous avez un projet immobilier :'),
      ];

    case FIRST_CONTACT_TEMPLATE.TRANSACTION_V1:
      return [
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'En tant que référent mutation-location au sein de votre syndic, je suis votre interlocuteur dédié pour toutes les questions liées à la vente ou à la location d\'un lot. À ce titre, je souhaite attirer votre attention sur un point souvent méconnu lors de la mise en vente d\'un appartement en copropriété.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'À l\'occasion de toute vente, le notaire demande au syndic un document appelé « état daté ». Ce document, remis à l\'acquéreur avant la signature de l\'acte définitif, récapitule notamment : les charges courantes et le budget de la copropriété, les sommes restant dues au syndicat, les travaux votés et à venir — dont ceux inscrits au plan pluriannuel de travaux (PPPT) — ainsi que les éventuelles procédures en cours.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Si l\'acquéreur découvre à ce stade des travaux votés ou un niveau de charges qu\'il n\'avait pas anticipés, la vente peut s\'en trouver fragilisée : renégociation du prix convenu, retard, voire remise en cause de la transaction.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'C\'est pourquoi une estimation réalisée sans connaissance précise de la copropriété (PPPT, travaux votés, budget, procédures) peut se révéler éloignée du prix réellement réalisable.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'En tant que « Référent Mutation Location », mon rôle est de vous informer au plus près de votre copropriété en vous envoyant un rapport sur votre immeuble. Vous y trouverez notamment les premiers éléments concernant le marché immobilier local, les ventes réalisées dans un rayon de 150 mètres autour de l\'immeuble au cours des quatre dernières années (extrait du fichier impots.gouv) et d\'autres informations utiles.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'À la suite de cette réception, je pourrai, si vous le souhaitez, échanger avec vous pour vous commenter ce document et répondre à vos questions si vous avez un projet de vente.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Si vous souhaitez ensuite aller plus loin — ou si vous disposez déjà d\'une estimation et souhaitez en obtenir une seconde, complémentaire, intégrant cette fois les informations propres à votre immeuble et à votre bien — je pourrai, après notre échange, si vous le souhaitez, convenir avec vous de la visite d\'une personne spécialisée du cabinet, qui se mettra en relation avec votre gestionnaire pour réaliser cette estimation en tenant compte de la vie de votre immeuble.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Cette démarche est entièrement facultative et sans engagement : vos coordonnées ne seront communiquées à la personne en charge de l\'estimation que si vous en faites vous-même la demande.' })],
          spacing: sp,
        }),
        consentParagraph('Pour toute question, ou pour recevoir le rapport de votre immeuble, vous pouvez indiquer vos préférences via le lien suivant :'),
      ];

    case FIRST_CONTACT_TEMPLATE.TRANSACTION_V2:
      return [
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'En tant que référent mutation-location au sein de votre syndic, je suis votre interlocuteur dédié pour toutes les questions liées à la vente ou à la location d\'un lot. À ce titre, je souhaite attirer votre attention sur les points souvent méconnus lors de la mise en vente d\'un appartement en copropriété.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: '1 / Les diagnostics : ils sont obligatoires pour la mise en vente. S\'ils sont purement informatifs, ils peuvent devenir des points bloquants pour votre acquéreur. Il est donc impératif de bien les interpréter pour estimer un bien.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: '2 / L\'état daté : à l\'occasion de toute vente, le notaire demande au syndic un document appelé « état daté ». Ce document, remis à l\'acquéreur avant la signature de l\'acte définitif, récapitule notamment : les charges courantes et le budget de la copropriété, les sommes restant dues au syndicat, les travaux votés et à venir — dont ceux inscrits au plan pluriannuel de travaux (PPPT) — ainsi que les éventuelles procédures en cours.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'C\'est pourquoi une estimation réalisée sans connaissance précise de la copropriété (PPPT, travaux votés, budget, procédures) peut se révéler éloignée du prix réellement réalisable.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: '3 / Les travaux de copropriété : si l\'acquéreur découvre à ce stade des travaux votés ou un niveau de charges qu\'il n\'avait pas anticipés, la vente peut s\'en trouver fragilisée : renégociation du prix convenu, retard, voire remise en cause de la transaction.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: '4 / Les prix de vente dans votre immeuble et alentours : dans un marché en évolution, il est impératif de bien maîtriser les valeurs de ventes réalisées dans un rayon proche. Le prix de mise en vente doit se situer dans la valeur marché pour éviter une perte de l\'intérêt commercial de votre bien.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Pour vous permettre d\'y voir plus clair, je peux, dans un premier temps et sur simple demande, vous adresser le rapport de copropriété de votre immeuble. Vous y trouverez déjà des éléments intéressants sur le marché immobilier : les ventes réalisées dans un rayon de 150 mètres autour de l\'immeuble au cours des quatre dernières années.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'À la suite de cette réception, je pourrai, si vous le souhaitez, échanger avec vous pour vous commenter ce document et répondre à vos questions si vous avez un projet de vente.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Si vous disposez déjà d\'une estimation mais souhaitez en obtenir une seconde, complémentaire, intégrant cette fois les informations propres à votre immeuble — je pourrai, après notre échange, si vous le souhaitez, convenir avec vous de la visite d\'une personne spécialisée du cabinet, qui se mettra en relation avec votre gestionnaire pour réaliser cette estimation en tenant compte de la vie de votre immeuble.' })],
          spacing: sp,
        }),
        new Paragraph({
          alignment: justified,
          children: [new TextRun({ text: 'Cette démarche est entièrement facultative et sans engagement : vos coordonnées ne seront communiquées à la personne en charge de l\'estimation que si vous en faites vous-même la demande.' })],
          spacing: sp,
        }),
        consentParagraph('Pour toute question, ou pour recevoir le rapport de votre immeuble, vous pouvez indiquer vos préférences via le lien suivant :'),
      ];
  }
};

/**
 * Generate unified DOCX letter content for any owner type.
 * All 4 templates share the same structure — only the body section differs.
 */
const generateLetterContent = (
  owner: Owner,
  companyName: string,
  referentName: string,
  today: string,
  mailingAddress: { street: string; postalCode: string; city: string },
  addressLine: string,
  consentUrl: string,
  qrCodeBase64: string | null,
  logoBase64: string | null,
  templateType: FirstContactTemplateType,
): (Paragraph | Table)[] => {
  const elements: (Paragraph | Table)[] = [];

  // Company logo
  if (logoBase64) {
    elements.push(
      new Paragraph({
        children: [
          new ImageRun({
            data: Uint8Array.from(atob(logoBase64), c => c.charCodeAt(0)),
            transformation: { width: 120, height: 60 },
            type: 'png',
          }),
        ],
        spacing: { after: 100 },
      }),
    );
  }

  // Sender info
  elements.push(
    new Paragraph({ children: [new TextRun({ text: companyName, bold: true })] }),
    new Paragraph({
      children: [new TextRun({ text: today, color: '666666', size: 20 })],
      spacing: { after: 100 },
    }),
  );

  // Spacer before address
  elements.push(new Paragraph({ children: [], spacing: { after: mmToTwips(5) } }));

  // Recipient address block
  elements.push(createAddressBlock(owner, mailingAddress));

  // Spacer after address
  elements.push(new Paragraph({ children: [], spacing: { after: mmToTwips(20) } }));

  // Subject line
  const isTransaction = isTransactionWithoutDpeTemplate(templateType);
  const subjectText = templateType === FIRST_CONTACT_TEMPLATE.TRANSACTION_V1
    ? 'Vendre votre appartement : bien estimer le prix en tenant compte de l\'état daté de votre bien'
    : templateType === FIRST_CONTACT_TEMPLATE.TRANSACTION_V2
      ? 'Vendre votre appartement : comment estimer le prix de votre bien avant une mise en vente ?'
      : 'Informations suite à la réalisation d\'un DPE d\'un logement aux caractéristiques similaires au vôtre';

  elements.push(
    new Paragraph({
      children: [
        new TextRun({ text: 'Objet : ', bold: true }),
        new TextRun({ text: subjectText, bold: true }),
      ],
    }),
    new Paragraph({
      children: [new TextRun({ text: addressLine, italics: true })],
      spacing: { after: 400 },
    }),
  );

  // Greeting
  elements.push(
    new Paragraph({
      children: [new TextRun({ text: isTransaction ? 'Madame, Monsieur,' : 'Cher Copropriétaire,' })],
      spacing: { after: 300 },
    }),
  );

  // Shared DPE opening paragraphs (skipped for transaction templates without DPE notions)
  if (!isTransaction) {
    elements.push(
      new Paragraph({
        alignment: AlignmentType.JUSTIFIED,
        children: [new TextRun({
          text: 'Je vous contacte en ma qualité de référent mutation location du syndic de votre immeuble et mon rôle est d\'être à votre disposition pour vous informer et vous guider quant à vos obligations et les documents obligatoires à fournir en cas de vente ou de location de votre bien immobilier.',
        })],
        spacing: { after: 240 },
      }),
      new Paragraph({
        alignment: AlignmentType.JUSTIFIED,
        children: [new TextRun({
          text: 'Dans ce cadre, nous avons remarqué qu\'un « Diagnostic de Performance Énergétique » (DPE) vient d\'être réalisé récemment pour un bien aux caractéristiques similaires au vôtre (si vous n\'êtes pas à l\'origine de ce DPE, merci de ne pas tenir compte de ce message).',
        })],
        spacing: { after: 240 },
      }),
      new Paragraph({
        alignment: AlignmentType.JUSTIFIED,
        children: [
          new TextRun({
            text: 'En tant que syndic, nous collectons ces informations publiées par l\'ADEME, afin de préparer et suivre, dans l\'intérêt de la copropriété, le DPE collectif et le PPT (plan pluriannuel de travaux) de votre immeuble. Cette démarche vous a été présentée dans la newsletter adressée aux copropriétaires.',
          }),
        ],
        spacing: { after: 240 },
      }),
    );
  }

  // Template-specific body
  elements.push(...buildTemplateBodyParagraphs(templateType, consentUrl));

  // Spacing before QR section
  elements.push(new Paragraph({ children: [], spacing: { after: 160 } }));

  // QR Code section
  elements.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      shading: { fill: 'F8F9FA' },
      border: {
        top: { style: BorderStyle.SINGLE, size: 1, color: 'DDDDDD' },
        bottom: { style: BorderStyle.SINGLE, size: 1, color: 'DDDDDD' },
        left: { style: BorderStyle.SINGLE, size: 1, color: 'DDDDDD' },
        right: { style: BorderStyle.SINGLE, size: 1, color: 'DDDDDD' },
      },
      children: [new TextRun({ text: 'Prendre un rendez-vous', bold: true })],
      spacing: { before: 200, after: 100 },
    }),
  );

  if (qrCodeBase64) {
    elements.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        shading: { fill: 'F8F9FA' },
        children: [
          new ImageRun({
            data: Uint8Array.from(atob(qrCodeBase64), c => c.charCodeAt(0)),
            transformation: { width: 100, height: 100 },
            type: 'png',
          }),
        ],
        spacing: { after: 100 },
      }),
    );
  }

  elements.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      shading: { fill: 'F8F9FA' },
      children: [new TextRun({ text: 'Scannez ce QR code pour indiquer vos préférences de contact', italics: true, size: 18 })],
      spacing: { after: 100 },
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      shading: { fill: 'F8F9FA' },
      children: [
        new TextRun({ text: 'Ou rendez-vous sur : ', size: 18 }),
        new ExternalHyperlink({
          link: consentUrl,
          children: [new TextRun({ text: consentUrl, size: 18, color: '0066CC', underline: {} })],
        }),
      ],
      spacing: { after: 200 },
    }),
  );

  // Closing
  const closingText = isTransaction
    ? 'Je reste à votre disposition,'
    : templateType === FIRST_CONTACT_TEMPLATE.BOTH_REPORTS
      ? 'Cordialement,'
      : 'Veuillez agréer, Cher Copropriétaire, l\'expression de nos salutations distinguées.';
  elements.push(
    new Paragraph({
      children: [new TextRun({
        text: closingText,
      })],
      spacing: { before: 400, after: 100 },
    }),
    new Paragraph({
      children: [new TextRun({ text: referentName, bold: true })],
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Référent Mutation Location' })],
    }),
    new Paragraph({
      children: [new TextRun({ text: companyName })],
      spacing: { after: 400 },
    }),
  );

  // RGPD footer
  elements.push(
    new Paragraph({
      border: { top: { style: BorderStyle.SINGLE, size: 1, color: 'DDDDDD' } },
      children: [new TextRun({
        text: 'Conformément au RGPD, vous disposez d\'un droit d\'accès, de rectification, d\'effacement et de portabilité de vos données. Vous pouvez exercer ces droits en nous contactant.',
        size: 18,
        color: '666666',
      })],
      spacing: { before: 200 },
    }),
  );

  return elements;
};

/**
 * Generate and download a DOCX letter for an owner.
 */
export const generateOwnerLetterDocx = async(
  owner: Owner,
  company: Company | null,
  templateType: FirstContactTemplateType = FIRST_CONTACT_TEMPLATE.REFERENT_ONLY,
  jwt: string | null = null,
): Promise<void> => {
  const consentUrl = await buildShortConsentUrl(company, owner.uuid, jwt);
  const addressLine = getPropertyAddress(owner);
  const today = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  
  const companyName = company?.brandingSettings?.commercialName || company?.name || 'Votre syndic';
  const referentName = resolveReferentName(owner, company);
  const companyLogo = company?.brandingSettings?.brandingImageName || null;
  const mailingAddress = getMailingAddress(owner);
  
  const [qrCodeBase64, logoBase64] = await Promise.all([
    fetchQRCodeAsBase64(consentUrl, 150),
    companyLogo ? fetchImageAsBase64(companyLogo) : Promise.resolve(null),
  ]);
  
  const contentParagraphs = generateLetterContent(
    owner, companyName, referentName, today, mailingAddress, addressLine,
    consentUrl, qrCodeBase64, logoBase64, templateType,
  );
  
  const doc = new Document({
    sections: [{
      properties: {
        page: {
          margin: {
            top: mmToTwips(15),
            right: mmToTwips(15),
            bottom: mmToTwips(20),
            left: mmToTwips(20),
          },
        },
      },
      children: contentParagraphs,
    }],
  });
  
  const blob = await Packer.toBlob(doc);
  const ownerName = `${owner.lastname || 'proprietaire'}`.replaceAll(/[^a-zA-Z0-9]/g, '_');
  const letterPrefix = isTransactionWithoutDpeTemplate(templateType) ? 'Courrier_Estimation' : 'Courrier_DPE';
  const filename = `${letterPrefix}_${ownerName}_${new Date().toISOString().split('T')[0]}.docx`;
  
  saveAs(blob, filename);
};

export default generateOwnerLetterDocx;
